import { Controller } from "@hotwired/stimulus";
import { Chart } from "chart.js";
import "chartjs-adapter-date-fns";
import { startOfWeek, endOfWeek, format } from "date-fns";

export default class extends Controller {
  static values = { data: Array };

  connect() {
    this.createMatrixChart();
    // Add resize listener
    window.addEventListener("resize", this.handleResize);
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy();
    }
    // Remove resize listener
    window.removeEventListener("resize", this.handleResize);
  }

  handleResize = () => {
    if (this.chart) {
      this.chart.destroy();
      this.createMatrixChart();
    }
  };

  createMatrixChart() {
    // Get container dimensions
    const containerWidth =
      this.element.parentElement.getBoundingClientRect().width ||
      this.element.getBoundingClientRect().width ||
      this.element.offsetWidth ||
      800;

    const containerHeight =
      this.element.parentElement.getBoundingClientRect().height ||
      this.element.offsetHeight ||
      400;

    // Calculate optimal number of weeks to show based on available width
    const idealSquareSize = 36; // Target square size in pixels
    const gutterSize = 2; // Allowance for space between squares
    const totalSquareSize = idealSquareSize + gutterSize;

    // Buffer for labels and spacing (left padding for day labels, top/bottom for month labels)
    const labelBuffer = 60; // Reduced buffer to give more space for cells

    // Calculate available space for the chart area
    const availableWidth = containerWidth - labelBuffer;

    // Calculate how many weeks we can fit horizontally, with some extra space
    const maxWeeks = Math.ceil(availableWidth / totalSquareSize);

    // Ensure we show at least 4 weeks (1 month) and at most 52 weeks (1 year)
    const weeksToShow = Math.max(4, Math.min(maxWeeks, 52));

    // Convert weeks to approximate months for data filtering
    const monthsToShow = Math.ceil(weeksToShow / 4.33); // 4.33 weeks per month average

    // console.log("Container dimensions:", { containerWidth, containerHeight });
    // console.log("Space calculation:", {
    //   availableWidth,
    //   maxWeeks,
    //   weeksToShow,
    //   monthsToShow,
    // });

    // Filter data based on calculated months
    const filteredData = this.filterDataByMonths(this.dataValue, monthsToShow);

    this.createChartWithData(filteredData, monthsToShow, weeksToShow);
  }

  filterDataByMonths(dataValue, monthsToShow) {
    if (!dataValue || dataValue.length === 0) return dataValue;

    const dataset = dataValue[0];
    if (!dataset.data || !Array.isArray(dataset.data)) return dataValue;

    // Calculate the cutoff date (monthsToShow months ago)
    const cutoffDate = new Date();
    cutoffDate.setMonth(cutoffDate.getMonth() - monthsToShow);

    // Find the Sunday of the week that comes after the cutoff date
    const sundayAfterCutoff = startOfWeek(cutoffDate, { weekStartsOn: 0 });
    sundayAfterCutoff.setDate(sundayAfterCutoff.getDate() + 7); // Add a week to get the next Sunday

    // Filter data points to only include those from the Sunday after cutoff onwards
    const filteredData = dataset.data.filter((point) => {
      if (point.x && typeof point.x === "string") {
        const pointDate = new Date(point.x);
        return pointDate >= sundayAfterCutoff;
      }
      return true; // Keep points without valid dates
    });

    // Return the filtered dataset
    return [
      {
        ...dataset,
        data: filteredData,
      },
    ];
  }

  createChartWithData(dataValue, monthsToShow, weeksToShow) {
    const ctx = this.element.getContext("2d");

    // Transform data for matrix chart format
    let matrixData = [];

    if (dataValue && dataValue.length > 0) {
      const dataset = dataValue[0];
      //console.log("Dataset:", dataset);
      if (dataset.data && Array.isArray(dataset.data)) {
        //console.log("Dataset data:", dataset.data);
        matrixData = dataset.data
          .map((point) => {
            if (
              typeof point === "object" &&
              point.x !== undefined &&
              point.y !== undefined &&
              point.d !== undefined &&
              point.v !== undefined
            ) {
              return point;
            } else {
              console.warn("Invalid data point:", point);
              return null;
            }
          })
          .filter((point) => point !== null);
      }
    }

    // Store data for use in callbacks
    const chartData = dataValue;
    const config = chartData[0] || {};

    // Use configuration from controller or fallback to defaults
    const energyLabels = config.energy_labels || [
      "Empty",
      "Low",
      "OK",
      "Full",
      "Way too much",
    ];

    const columnLabels = config.column_labels || [
      "Sun",
      "Sat",
      "Fri",
      "Thu",
      "Wed",
      "Tue",
      "Mon",
    ];

    const colors = config.colors || {
      0: "#c4b5fd",
      1: "#a78bfa",
      2: "#8b5cf6",
      3: "#6d28d9",
      4: "#2e1065",
    };

    const hoverColors = config.hover_colors || {
      0: "#e8e4f5",
      1: "#d8d1f0",
      2: "#c8bde8",
      3: "#b8a9e0",
      4: "#a895d8",
    };

    const emptyColor = config.empty_color || "rgba(200, 200, 200, 0.1)";
    const tooltipConfig = config.tooltip || {
      title_icon: "📅",
      value_icon: "⚡",
      empty_text: "No check-in",
    };

    // Calculate optimal chart size for square cells
    const containerWidth =
      this.element.parentElement.getBoundingClientRect().width;
    const containerHeight =
      this.element.parentElement.getBoundingClientRect().height;

    const numDays = columnLabels.length;

    // Calculate the maximum cell size that fits in the container
    // Account for padding and labels
    const padding = 80; // Total padding (left + right + top + bottom)
    const idealSquareSize = 36;

    // Calculate available space for the chart area
    const availableWidth = containerWidth - padding;
    const availableHeight = containerHeight - padding;

    // Calculate the maximum cell size that fits in the container
    const maxCellSizeByWidth = availableWidth / weeksToShow;
    const maxCellSizeByHeight = availableHeight / numDays;

    // Use the ideal square size if it fits, otherwise use the calculated max
    const finalCellWidth = Math.min(idealSquareSize, maxCellSizeByWidth);
    const finalCellHeight = Math.min(idealSquareSize, maxCellSizeByHeight);

    // console.log("Cell size calculation:", {
    //   availableWidth,
    //   availableHeight,
    //   weeksToShow,
    //   numDays,
    //   maxCellSizeByWidth,
    //   finalCellWidth,
    //   finalCellHeight,
    // });

    // Calculate the optimal chart dimensions
    const chartWidth = weeksToShow * finalCellWidth;
    const chartHeight = numDays * finalCellHeight;

    // Set canvas size to match the calculated dimensions
    this.element.width = chartWidth + padding;
    this.element.height = chartHeight + padding;
    this.element.style.width = `${chartWidth + padding}px`;
    this.element.style.height = `${chartHeight + padding}px`;

    this.chart = new Chart(ctx, {
      type: "matrix",
      data: {
        datasets: [
          {
            label: config.name || "Energy Levels",
            data: matrixData,
            config: config, // Add config to dataset for legend access
            categoryPercentage: 1.0,
            barPercentage: 1.0,
            backgroundColor({ raw }) {
              if (raw.v === null || raw.v === undefined || raw.v === -1) {
                return emptyColor;
              }

              return colors[raw.v] || emptyColor;
            },
            hoverBackgroundColor: ({ raw }) => {
              if (raw.v === null || raw.v === undefined || raw.v === -1) {
                return "rgba(232, 232, 232, 1)";
              }
              return hoverColors[raw.v] || "rgba(255, 255, 255, 0.3)";
            },
            borderWidth: 0,
            borderRadius: 4,
            width: finalCellWidth * 0.75,
            height: finalCellHeight,
          },
        ],
      },
      options: {
        onClick: (e) => {
          const activePoints = this.chart.getElementsAtEventForMode(
            e,
            "nearest",
            { intersect: true },
            true,
          );
          if (activePoints.length > 0) {
            const point = activePoints[0];
            const data =
              this.chart.data.datasets[point.datasetIndex].data[point.index];
            if (data && data.url) {
              const link = document.getElementById("matrix-chart-sidebar-link");
              if (link) {
                link.href = data.url;
                link.click();
              }
            }
          }
        },
        animation: false,
        responsive: false, // Disable responsive to maintain our calculated size
        maintainAspectRatio: false,
        hover: {
          mode: "nearest",
          intersect: true,
          animationDuration: 0, // Disable hover animations
        },
        layout: {
          padding: {
            top: 20,
            bottom: 20,
            left: 40,
            right: 20,
          },
          autoPadding: false,
        },
        plugins: {
          legend: {
            display: false, // Hide the built-in legend
          },
          datalabels: {
            display: false,
          },
          tooltip: {
            displayColors: false,
            callbacks: {
              title() {
                return "";
              },
              label(context) {
                const v = context.dataset.data[context.dataIndex];
                if (!v) return "";

                // Format the date as a sentence (e.g., "March 15th 2025")
                const date = new Date(v.d);
                const formattedDate = format(date, "MMMM do yyyy");

                return [
                  `${tooltipConfig.title_icon} ${formattedDate}`,
                  `${tooltipConfig.value_icon} Energy: ${
                    v.v === null || v.v === -1
                      ? tooltipConfig.empty_text
                      : energyLabels[v.v] || "No data"
                  }`,
                ];
              },
            },
          },
        },
        scales: {
          x: {
            type: "time",
            position: "bottom",
            offset: true,
            border: {
              display: false,
            },
            grid: {
              display: false,
              drawBorder: false,
              tickLength: 0,
            },
            time: {
              unit: "week",
              round: "week",
              isoWeekday: 1,
              displayFormats: {
                week: "MMM d",
              },
            },
            distribution: "linear",
            ticks: {
              maxRotation: 0,
              autoSkip: false,
              padding: 1,
              font: {
                family: "monospace",
              },
              callback: function (value, index) {
                // Only show month label for the first week of each month
                const date = new Date(value);
                const weekStart = startOfWeek(date, { weekStartsOn: 1 }); // Monday
                const weekEnd = endOfWeek(date, { weekStartsOn: 1 }); // Sunday

                // Check if the week contains any date that's the 1st of a month
                let isFirstWeekOfMonth = false;
                let firstDayDate = null;

                for (
                  let d = new Date(weekStart);
                  d <= weekEnd;
                  d.setDate(d.getDate() + 1)
                ) {
                  if (d.getDate() === 1) {
                    isFirstWeekOfMonth = true;
                    firstDayDate = new Date(d);
                    break;
                  }
                }

                if (isFirstWeekOfMonth) {
                  const monthLabel = format(firstDayDate, "MMM");
                  return monthLabel;
                } else {
                  return "";
                }
              },
            },
            title: {
              display: false,
            },
          },
          y: {
            type: "category",
            position: "left",
            offset: true,
            border: {
              display: false,
            },
            labels: columnLabels,
            grid: {
              display: false,
              drawBorder: false,
            },
            ticks: {
              callback: function (value, index) {
                return columnLabels[index];
              },
              autoSkip: false,
              maxTicksLimit: columnLabels.length,
              padding: 0,
              source: "labels",
              font: {
                family: "monospace",
              },
            },
            title: {
              display: false,
            },
          },
        },
      },
    });

    // Create custom legend outside the chart
    this.createCustomLegend();
  }

  createCustomLegend() {
    // Check if legend already exists
    const existingLegend = this.element.parentElement.querySelector(
      "[data-matrix-legend]",
    );
    if (existingLegend) {
      return; // Legend already exists, don't create another one
    }

    const config = this.dataValue[0] || {};
    const energyLabels = config.energy_labels || [
      "Empty",
      "Low",
      "OK",
      "Full",
      "Way too much",
    ];
    const colors = config.colors || {
      0: "#D1B8F2",
      1: "#C7A7F1",
      2: "#B383F1",
      3: "#9E5CF5",
      4: "#6c0fe6",
    };

    // Create legend container
    const legendContainer = document.createElement("div");
    legendContainer.setAttribute("data-matrix-legend", "true"); // Add identifier
    legendContainer.style.cssText = `
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 10px;
      padding: 2px 10px;
      font-size: 12px;
      color: #797878;
      width: 100%;
    `;

    // Create legend items
    energyLabels.forEach((label, index) => {
      const legendItem = document.createElement("div");
      legendItem.style.cssText = `
        display: flex;
        align-items: center;
        gap: 4px;
      `;

      const marker = document.createElement("div");
      marker.style.cssText = `
        width: 12px;
        height: 12px;
        background-color: ${colors[index] || "#ccc"};
        border-radius: 2px;
        flex-shrink: 0;
      `;

      const text = document.createElement("span");
      text.textContent = label;

      legendItem.appendChild(marker);
      legendItem.appendChild(text);
      legendContainer.appendChild(legendItem);
    });

    // Add legend to the chart container (insert before the chart to place it above)
    this.element.parentElement.insertBefore(legendContainer, this.element);

    // Ensure the parent container uses flexbox with column direction
    this.element.parentElement.style.display = "flex";
    this.element.parentElement.style.flexDirection = "column";
    this.element.parentElement.style.alignItems = "stretch";
  }
}
